import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  RootState,
  useProductName,
  parseTextWithHTML

} from '@rb-serve-app/core';
import Breadcrumbs from '../../components/BreadCrumbs';
import Button from '../../components/Button';
import CommanModal from '../../components/CommanModal';
import { logBrazeCustomEvent } from '../../config/braze';

const CardFeatures = () => {
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData || { entries: {}, assets: {} };
  const { accountDetails } = useSelector((state: RootState) => state.user);

  // Use the useProductName hook to get card structure, which includes image URL
  const { cardStructure } = useProductName();
  const { rb_common, rb_feature_flagging } = entries || {};

  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    logBrazeCustomEvent('viewed_card_features');
  }, []);

  const toggleDisclaimer = () => {
    setDisclaimerExpanded(!disclaimerExpanded);
  };

  // Get the viewCardFeaturesNavTitle from contentful
  const { rb_marketing_tiles } = entries || {};
  const breadcrumbs = [
    { name: rb_marketing_tiles?.moreTitle, href: '/' },
    {
      name: rb_marketing_tiles?.viewCardFeaturesNavTitle,
      href: '/card-features',
    },
  ];
  const headerTitle =
    rb_marketing_tiles?.viewCardFeaturesNavTitle || 'Card Features';

  // Get card features data
  const cardFeaturesData = rb_marketing_tiles?.cardFeaturesData || [];

  // Find the matching card data based on productName
  const userProductName = accountDetails?.productName || '';

  // Only proceed if we have both user data and contentful data loaded
  const isDataLoaded = userProductName && cardFeaturesData.length > 0;

  // Get card data dynamically by finding matching product name
  let cardData = null;

  if (isDataLoaded) {
    cardData = cardFeaturesData.find(
      (card: any) => card.productName === userProductName
    );

    // Only fall back to first item if no match found AND we have a valid productName
    if (!cardData && userProductName) {
      cardData = cardFeaturesData[0];
    }
  }

  // If card data is still undefined, use a default empty structure
  if (!cardData) {
    cardData = {
      data: {
        name: '',
        card_details: {
          title: 'Card Features',
          benefits: [],
          additional_info: {
            text: 'See Benefits Guide',
            guide_link: '#',
            legal_disclaimer: '[+] Legal disclaimer',
          },
        },
      },
    };
  }

  const handleBenefitsGuide = () => {
    setShowExitModal(true);
  };

  // Function to handle "Continue" button in the exit modal
  const handleContinue = () => {
    setShowExitModal(false);
    if (cardData?.data?.card_details?.additional_info?.guide_link) {
      window.open(
        cardData.data.card_details.additional_info.guide_link,
        '_blank'
      );
    }
  };

  // Function to handle "Stay" button in the exit modal
  const handleStay = () => {
    setShowExitModal(false);
  };

  const addSuperscriptText = (text: string): React.ReactNode => {
    if (text === null || text === undefined) return text;
    const safeText = typeof text === 'string' ? text : String(text);
    const preReplaced = safeText
      .replace(/MD/g, '<sup>MD</sup>')
      .replace(/®/g, '<sup>®</sup>');
    return parseTextWithHTML(preReplaced, 'text-[#2F6FA7] hover:underline');
  };

  const renderBannerText = (text: string): React.ReactNode => {
    if (text === null || text === undefined) return text;

    const safeText = typeof text === 'string' ? text : String(text);
    const linkTagPattern = /<link\s+url=['"]([^'"]+)['"]>([\s\S]*?)<\/link>/i;
    const match = safeText.match(linkTagPattern);

    if (!match) {
      return addSuperscriptText(safeText);
    }

    const [fullMatch, rawUrl, linkText] = match;
    const startIndex = match.index || 0;
    const before = safeText.slice(0, startIndex);
    const after = safeText.slice(startIndex + fullMatch.length);
    const afterWithSpace =
      after && !/^\s/.test(after) ? ` ${after}` : after;
    const href = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;

    return (
      <>
        {addSuperscriptText(before)}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#2F6FA7] hover:underline cursor-pointer mr-1"
        >
          {addSuperscriptText(linkText)}
        </a>
        {addSuperscriptText(afterWithSpace)}
      </>
    );
  };

  const stripSuperscripts = (s: string): string => {
    return (
      (s || '')
        // Remove entity-encoded <sup>…</sup>
        .replace(/&lt;sup[^&gt;]*&gt;.*?&lt;\/sup&gt;/gi, '')
        // Remove actual HTML <sup>…</sup>
        .replace(/<sup[^>]*>.*?<\/sup>/gi, '')
        // Remove Unicode superscripts ⁰¹²³⁴⁵⁶⁷⁸⁹ including ¹²³
        .replace(/[\u00B2\u00B3\u00B9\u2070-\u2079]/g, '')
    );
  };

  const renderDescriptionParagraphs = (
    desc: string,
    customClass?: string
  ): React.ReactNode => {
    const parts = desc.split(/\n/);
    const textClassName =
      customClass ||
      'text-base md:text-lg text-gray-800 mb-4 max-w-4xl mx-auto leading-7 font-normal';

    return parts.map((part, idx) => (
      <h5 key={idx} aria-label={part} className={textClassName}>
        {addSuperscriptText(part)}
      </h5>
    ));
  };

  const renderTravelPlanDescription = (desc: string) => {
    const lines = desc.split('\n');
    const mainList: {
      content: React.ReactNode;
      sub?: any[];
    }[] = [];

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // LEVEL 1 - bullet point
      if (trimmed.startsWith('•')) {
        mainList.push({
          content: parseTextWithHTML(
            trimmed.replace('•', '').trim(),
            'text-[#DA291C] underline'
          ),
          sub: [],
        });
      }
      // LEVEL 2 - circle
      else if (trimmed.startsWith('◦')) {
        const lastMain = mainList[mainList.length - 1];
        if (lastMain) {
          lastMain.sub?.push({
            content: parseTextWithHTML(
              trimmed.replace('◦', '').trim(),
              'text-[#DA291C] underline'
            ),
            ordered: [],
          });
        }
      }
      // LEVEL 3 - numbered
      else if (/^\d+\./.test(trimmed)) {
        const lastMain = mainList[mainList.length - 1];
        const lastSub = lastMain?.sub?.[lastMain.sub.length - 1];
        if (lastSub) {
          lastSub.ordered.push(
            parseTextWithHTML(
              trimmed.replace(/^\d+\.\s*/, ''),
              'text-[#DA291C] underline'
            )
          );
        }
      }
    });

    return (
      <ul className="list-disc ml-6 text-base leading-6 text-gray-800">
        {mainList.map((item, i) => (
          <li key={i}>
            {item.content}
            {item.sub && item.sub.length > 0 && (
              <ul className="list-[circle] ml-6 mt-2 break-words">
                {item.sub.map((subItem: any, j: number) => (
                  <li key={j}>
                    {subItem.content}
                    {subItem.ordered && subItem.ordered.length > 0 && (
                      <ol className="list-decimal ml-6 mt-2">
                        {subItem.ordered.map((ord: any, k: number) => (
                          <li key={k}>{ord}</li>
                        ))}
                      </ol>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  };

  function ListItem({ icon, alt = '', text, textClassName = '' }: any) {
    return (
      <div className="flex items-center gap-3 ">
        <img src={icon} alt={alt} className="w-[30px] h-[30px] mt-1" />
        <p className={`text-[16px] leading-5 text-gray-800 bg-color-none`}>
          {stripSuperscripts(text)}
        </p>
      </div>
    );
  }

  const keyBenefitIcons = [
    assets?.auto_pay,
    assets?.redemption_bonus,
    assets?.fx_fees,
    assets?.wireless,
    assets?.icn_home,
  ];

  const eligibleECIcons = [
    assets?.ignite_tv_box_with_remote,
    assets?.app_tv,
    assets?.free_show_and_movies,
  ];

  const annualFeeIcons = [assets?.fx_fees, assets?.credit_card];

  const insuranceBenefitsIcons = [
    assets?.flight_delay_protection,
    assets?.baggage_protection,
    assets?.drive_safety_protection,
    assets?.hotel_protection,
    assets?.purchase_protection,
    assets?.reliable_protection,
    assets?.flexible_protection,
    assets?.travel_protection,
  ];
  // Show loading state if data is not ready
  if (!isDataLoaded && (!userProductName || cardFeaturesData.length === 0)) {
    return (
      <>
        <div className="px-2 md:px-8 py-2">
          <Breadcrumbs items={breadcrumbs} />
        </div>

        <div className="px-2 md:px-4 lg:px-6">
          <div className="relative p-6 md:p-10 lg:p-12 max-w-6xl mx-auto rounded-3xl bg-gradient-to-b from-white to-gray-50 shadow-2xl">
            {/* Title Skeleton */}
            <div className="flex justify-center mb-12">
              <div className="h-10 bg-gray-200 rounded-lg w-80 animate-pulse"></div>
            </div>

            {/* Card Section Skeleton */}
            <div className="flex flex-col items-center mb-16">
              {/* Card Image Skeleton */}
              <div className="mb-8">
                <div className="w-[140px] h-[220px] bg-gray-200 rounded-xl animate-pulse"></div>
              </div>

              {/* Card Title Skeleton */}
              <div className="h-8 bg-gray-200 rounded-lg w-64 mb-4 animate-pulse"></div>

              {/* Card Subtitle Skeleton */}
              <div className="h-6 bg-gray-200 rounded-lg w-72 animate-pulse"></div>
            </div>

            {/* Benefits Section Skeleton */}
            <div className="space-y-8 mb-16">
              {[1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-xl border border-gray-100"
                >
                  {/* Benefit Title Skeleton */}
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-12 h-1 bg-gray-200 rounded-full mr-4 animate-pulse"></div>
                    <div className="h-8 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
                    <div className="w-12 h-1 bg-gray-200 rounded-full ml-4 animate-pulse"></div>
                  </div>

                  {/* Benefit Description Skeleton */}
                  <div className="text-center space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-full animate-pulse"></div>
                    <div className="h-5 bg-gray-200 rounded w-4/5 mx-auto animate-pulse"></div>
                    <div className="h-5 bg-gray-200 rounded w-3/4 mx-auto animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Icons Section Skeleton */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 mb-12 shadow-2xl border border-gray-200">
              <div className="flex justify-center items-center gap-32">
                {[1, 2].map((index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className="w-20 h-20 bg-gray-300 rounded-full mb-4 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded w-24 animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Legal Disclaimer Skeleton */}
            <div className="mb-8">
              <div className="h-5 bg-gray-200 rounded w-48 animate-pulse"></div>
            </div>

            {/* Button Skeleton */}
            <div className="text-center mb-12">
              <div className="h-16 bg-gray-200 rounded-lg w-64 mx-auto animate-pulse"></div>
            </div>
          </div>
        </div>
      </>
    );
  }
  console.log('Card data loaded:', cardData);
  const bannerURLbasedOnProductType =
    entries?.rb_marketing_tiles?.cardFeatureBanner?.[
      accountDetails?.productName
    ]?.title;
  return (
    <>
      {rb_marketing_tiles?.cardFeatureBanner &&
        rb_marketing_tiles?.cardFeatureBanner?.showBanner && 
        rb_marketing_tiles?.cardFeatureBanner?.bannerExclusionList?.includes(userProductName) === false && (
          <div className="mb-1 bg-[#E5F4F7] w-full">
            <div className="flex items-center gap-3 py-3 max-w-6xl px-5">
              <img
                src={assets?.bannerInfoIcon}
                alt="info-icon"
                className="w-5 h-5"
              />
              <p className=" font-medium text-[#1F1F1F] text-base">
                {renderBannerText(
                  bannerURLbasedOnProductType
                    ? bannerURLbasedOnProductType
                    : rb_marketing_tiles.cardFeatureBanner?.title
                )}
              </p>
            </div>
          </div>
        )}
      <div className="px-2 md:px-8 py-2">
        <Breadcrumbs items={breadcrumbs} />
      </div>

      <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 mb-4">
        {/* Main Container */}
        <div className="relative w-full p-4 sm:p-6 md:p-10 lg:p-12 max-w-6xl mx-auto rounded-3xl bg-gradient-to-b from-white to-gray-50 shadow-2xl hover:shadow-2xl transition-all duration-500">
          {/* Main Heading */}
          <h1
            aria-label={cardData?.data?.webName}
            className="text-2xl md:text-4xl font-bold mb-12 text-center text-[#000000]"
          >
            {addSuperscriptText(cardData?.data?.webName || 'Card Features')}
          </h1>

          {/* Card Section - Centered */}
          <div className="flex flex-col items-center mb-12">
            {/* Product Image */}
            <div className="mb-8">
              <img
                src={cardStructure.imgUrl}
                alt={`Img ${cardData?.data?.webName}`}
                className={`${
                  !cardStructure.isHorizontal
                    ? 'w-[140px] h-[220px]'
                    : 'h-[140px] w-[220px]'
                } object-contain transition-all duration-300 hover:scale-105 drop-shadow-xl`}
              />
            </div>

            {/* Card Title */}
            <h2
              aria-label={cardData?.data?.card_details?.webTitle}
              className="text-2xl font-bold text-center mb-4 text-[#000000]"
            >
              {addSuperscriptText(cardData?.data?.card_details?.webTitle || '')}
            </h2>
          </div>

          {/* Benefits Section - Material Design Style Key benefit */}

          {cardData?.data?.card_details?.key_benefits && (
            <h3
              aria-label=""
              className="text-[28px] leading-[32px] sm:text-xl md:text-2xl font-[700] text-[#DA291C] tracking-wide"
            >
              {addSuperscriptText(
                cardData?.data?.card_details?.key_benefits?.title
              )}
            </h3>
          )}

          {cardData?.data?.card_details?.key_benefits && (
            <main className="w-full flex gap-2 sm:gap-4 p-2 sm:p-4 md:p-6 lg:p-[22px] xl:flex-row lg:flex-row md:flex-col flex-col">
              {cardData?.data?.card_details?.key_benefits && (
                <div className="w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-y-4 gap-x-8">
                    {cardData?.data?.card_details?.key_benefits?.key_benefits_list?.map(
                      (benefit: any, index: number) => (
                        <div key={index} className="flex items-start gap-3">
                          <img
                            src={keyBenefitIcons[index] || ''}
                            alt="benefit-icon"
                            className="w-[28px] h-[28px] mt-1"
                          />
                          <p
                            className="text-[16px] leading-6 text-gray-800"
                            dangerouslySetInnerHTML={{ __html: benefit }}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              <section />
            </main>
          )}

          {/* ANNUAL FEE */}
          {cardData?.data?.card_details?.annual_fee?.title && (
            <h3
              aria-label={cardData?.data?.card_details?.annual_fee?.title}
              className="text-[28px] leading-[32px] sm:text-xl md:text-2xl font-[700] text-[#DA291C] tracking-wide"
            >
              {addSuperscriptText(
                cardData?.data?.card_details?.annual_fee?.title
              )}
            </h3>
          )}
          {cardData?.data?.card_details?.annual_fee?.length ||
            (cardData?.data?.card_details?.annual_fee
              ?.annual_interest_rate_web && (
              <main className="w-full flex gap-2 sm:gap-4 p-2 sm:p-4 md:p-6 lg:p-[22px] xl:flex-row lg:flex-row md:flex-col flex-col">
                {/* LEFT SIDE - 60% */}

                {/* Annual Fee */}
                <section className="xl:w-[60%] lg:w-[60%] md:w-full w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {cardData?.data?.card_details?.annual_fee?.annual_fee_list_web?.map(
                      (annualFeeValue: any, index: number) => (
                        <div
                          className="flex flex-col"
                          key={`${annualFeeValue?.title ?? 'fee'}-${index}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <img
                              src={annualFeeIcons?.[index] ?? ''}
                              alt={annualFeeValue?.title ?? ''}
                              className="w-[30px] h-[30px]"
                            />
                            <p className="text-gray-800 text-[16px] bg-color-none">
                              {annualFeeValue?.title}
                            </p>
                          </div>
                          {annualFeeValue?.value ? (
                            <p className="text-[20px] font-semibold text-primary-red bg-color-none">
                              {annualFeeValue.value}
                            </p>
                          ) : null}
                        </div>
                      )
                    )}
                  </div>
                </section>

                {/* RIGHT SIDE - 40% */}
                {cardData?.data?.card_details?.annual_fee
                  ?.annual_interest_rate_web && (
                  <section className="xl:w-[40%] lg:w-[40%] md:w-full w-full">
                    <div className="flex flex-col">
                      <div className="flex items-start gap-2 mb-2">
                        <img
                          src={assets?.fx_fees || ''}
                          alt="Interest rates"
                          className="w-[30px] h-[30px]"
                        />
                        <p className="text-gray-800 font-normal bg-color-none">
                          {addSuperscriptText(
                            cardData?.data?.card_details?.annual_fee
                              ?.annual_interest_rate_web?.title
                          )}
                        </p>
                      </div>

                      <p className="">
                        {renderDescriptionParagraphs(
                          cardData?.data?.card_details?.annual_fee
                            ?.annual_interest_rate_web?.description,
                          'text-base text-[16px] text-gray-800 max-w-4xl mx-auto leading-2 font-normal bg-color-none'
                        )}
                      </p>
                    </div>
                  </section>
                )}
              </main>
            ))}

          {/* Insurance Benefit */}
          {cardData?.data?.card_details?.insurance_benefits && (
            <h3
              aria-label=""
              className="text-[28px] leading-[32px] sm:text-xl md:text-2xl font-[700] text-[#DA291C] tracking-wide"
            >
              {addSuperscriptText(
                cardData?.data?.card_details?.insurance_benefits?.title
              )}
            </h3>
          )}

          {cardData?.data?.card_details?.insurance_benefits && (
            <main className="w-full flex gap-2 sm:gap-4 p-2 sm:p-4 md:p-6 lg:p-[22px]">
              <section className="w-full">
                {/* Keep your two-row look using 4 vertical columns (2 items per column) */}
                <div className="flex gap-2 md:gap-4 xl:gap-8 lg:gap-8 flex-col md:flex-col xl:flex-row lg:flex-row">
                  <ul
                    className="
            mt-6 grid gap-x-10 gap-y-6
            grid-cols-1
            sm:grid-cols-2
            lg:grid-cols-3
            xl:grid-cols-4
            "
                    aria-label="Insurance benefits"
                  >
                    {cardData?.data?.card_details?.insurance_benefits?.insurance_benefits_list?.map(
                      (benefit: any, index: number) => (
                        <li key={index}>
                          <ListItem
                            icon={insuranceBenefitsIcons?.[index] || ''}
                            alt={benefit}
                            text={benefit}
                          />
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </section>
            </main>
          )}
          {(cardData?.data?.card_details?.travel_benifits ||
            cardData?.data?.card_details?.other_perks) && (
            <div className="space-y-10 text-gray-700">
              {/* Travel Benefits */}
              {cardData?.data?.card_details?.travel_benifits && (
                <div>
                  <h2 className="text-[28px] leading-[32px] sm:text-xl md:text-2xl font-[700] text-[#DA291C] tracking-wide">
                    {cardData?.data?.card_details?.travel_benifits?.title}
                  </h2>

                  <section className="p-2 sm:p-4 md:p-6 lg:p-[22px]">
                    <h3
                      aria-label={
                        cardData?.data?.card_details?.travel_benifits?.title
                      }
                      className="text-[16px] font-normal tracking-wide"
                    >
                      {renderTravelPlanDescription(
                        cardData?.data?.card_details?.travel_benifits
                          ?.description
                      )}
                    </h3>
                  </section>
                </div>
              )}

              {/* Other Perks */}
              {cardData?.data?.card_details?.other_perks && (
                <div>
                  <h2 className="text-[28px] leading-[32px] sm:text-xl md:text-2xl font-[700] text-[#DA291C] tracking-wide">
                    {cardData?.data?.card_details?.other_perks?.title}
                  </h2>
                  <section className="p-2 sm:p-4 md:p-6 lg:p-[22px]">
                    <h3
                      aria-label={
                        cardData?.data?.card_details?.other_perks?.title
                      }
                      className="text-[16px] font-normal tracking-wide"
                    >
                      {renderTravelPlanDescription(
                        cardData?.data?.card_details?.other_perks?.description
                      )}
                    </h3>
                  </section>
                </div>
              )}
            </div>
          )}

          <div className="space-y-8 mb-16">
            {cardData?.data?.card_details?.benefits
              ?.filter(
                (benefit: any) =>
                  benefit && (benefit.category || benefit.description)
              )
              .map((benefit: any, index: number) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  {/* Benefit Category/Title - Material Design Typography */}
                  {benefit.category && (
                    <div className="flex items-center justify-center mb-6">
                      <div className="w-8 h-1 bg-gradient-to-r from-[#DA291C] to-[#FF6B35] rounded-full mr-3 md:w-12 md:mr-4"></div>
                      <h3
                        aria-label={benefit.category}
                        className="text-lg sm:text-xl md:text-2xl font-medium text-[#DA291C] tracking-wide"
                      >
                        {addSuperscriptText(benefit.category)}
                      </h3>
                      <div className="w-8 h-1 bg-gradient-to-r from-[#FF6B35] to-[#DA291C] rounded-full ml-3 md:w-12 md:ml-4"></div>
                    </div>
                  )}

                  {/* Benefit Description - Material Design Body Text */}
                  <div className="text-center">
                    {benefit.description &&
                      renderDescriptionParagraphs(benefit.description)}

                    {/* Alternate Description (if exists) */}
                    {benefit.alternate && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        {benefit?.otherwise && (
                          <p
                            aria-label={benefit.otherwise}
                            className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto leading-7 font-normal"
                          >
                            {addSuperscriptText(benefit.otherwise)}
                          </p>
                        )}
                        <p
                          aria-label={benefit.alternate}
                          className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto leading-7 font-normal"
                        >
                          {addSuperscriptText(benefit.alternate)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>

          {/* Additional Benefits Section - Material Design Style */}

          {cardData?.data?.card_details?.additional_benefits &&
            cardData?.data?.card_details?.additional_benefits?.length > 0 && (
              <div className="mb-16">
                {cardData?.data.card_details?.additional_benefits?.map(
                  (additionalBenefit: any, sectionIndex: number) => (
                    <div
                      key={sectionIndex}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 shadow-2xl hover:shadow-2xl border border-gray-100 mb-8 transition-all duration-300"
                    >
                      {/* Additional Benefit Category/Title - Material Design */}
                      {additionalBenefit.category && (
                        <div className="text-center mb-8 md:mb-12">
                          <div className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-[#DA291C] to-[#FF6B35] rounded-full mb-4 md:mb-6">
                            <h3
                              aria-label={additionalBenefit.category}
                              className="text-base sm:text-lg md:text-xl font-medium text-white tracking-wide"
                            >
                              {additionalBenefit.category}
                            </h3>
                          </div>
                        </div>
                      )}

                      {/* Additional Benefit Items */}
                      <div className="grid gap-8  md:gap-12">
                        {additionalBenefit?.items
                          ?.filter(
                            (item: any) =>
                              item && (item.feature || item.description)
                          )
                          .map((item: any, itemIndex: number) => (
                            <div
                              key={itemIndex}
                              className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl border border-gray-50 transition-all duration-300"
                            >
                              {/* Feature Title - Material Design */}
                              {item.feature && (
                                <div className="flex items-center mb-2 md:mb-4">
                                  <div className="w-2 h-2 bg-[#DA291C] rounded-full mr-3"></div>
                                  <h4
                                    aria-label={item.feature}
                                    className="text-lg md:text-xl font-medium text-[#DA291C] tracking-wide"
                                  >
                                    {item.feature}
                                  </h4>
                                </div>
                              )}

                              {/* Feature Description - Material Design Body Text */}
                              {item.description &&
                                (() => {
                                  const lines = item.description.split(/\n/);

                                  return (
                                    <p
                                      aria-label={item.description}
                                      className="text-sm md:text-base text-gray-700 leading-6 font-normal pl-5"
                                    >
                                      {lines.map((line: any, index: any) => (
                                        <span key={index}>
                                          {addSuperscriptText(line)}
                                          {index < lines.length - 1 && <br />}
                                        </span>
                                      ))}
                                    </p>
                                  );
                                })()}
                            </div>
                          ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

          {/* Icons Section - Material Design Card */}

          {userProductName !== rb_common?.commonString?.fidoProductName &&
            userProductName !== rb_common?.commonString?.platinumProductName &&
            userProductName !== rb_common?.commonString?.smbProductName &&
            userProductName !== rb_common?.commonString?.legendProductName && (
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-4 sm:p-6 md:p-8 mb-12 shadow-2xl hover:shadow-2xl border border-gray-200 transition-all duration-300">
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 md:gap-12 lg:gap-24">
                  <div className="flex flex-col items-center group cursor-pointer flex-shrink-0">
                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-[#DA291C] hover:bg-[#b32218] flex items-center justify-center mb-2 sm:mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                      <img
                        src={assets?.no_annual_fee_icon}
                        alt={`Img ${rb_marketing_tiles?.rbCardFeatureIcons?.Noannualfee}`}
                        className="w-8 sm:w-10 h-8 sm:h-10 brightness-0 invert"
                      />
                    </div>
                    <span
                      aria-label={
                        rb_marketing_tiles?.rbCardFeatureIcons?.Noannualfee ||
                        'No annual fee'
                      }
                      className="text-xs sm:text-sm font-medium text-center text-gray-800 group-hover:text-[#DA291C] transition-colors duration-300 tracking-wide max-w-[80px] sm:max-w-none"
                    >
                      {rb_marketing_tiles?.rbCardFeatureIcons?.Noannualfee}
                    </span>
                  </div>

                  <div className="flex flex-col items-center group cursor-pointer flex-shrink-0">
                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-[#DA291C] hover:bg-[#b32218] flex items-center justify-center mb-2 sm:mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                      <img
                        src={assets?.roam_like_home_icon}
                        alt={`Img ${rb_marketing_tiles?.rbCardFeatureIcons?.FreeRoamlikeHome}`}
                        className="w-8 sm:w-10 h-8 sm:h-10 brightness-0 invert"
                      />
                    </div>
                    <span
                      aria-label={
                        rb_marketing_tiles?.rbCardFeatureIcons
                          ?.FreeRoamlikeHome || 'Free Roam like Home'
                      }
                      className="text-xs sm:text-sm font-medium text-center text-gray-800 group-hover:text-[#DA291C] transition-colors duration-300 tracking-wide max-w-[80px] sm:max-w-none"
                    >
                      {rb_marketing_tiles?.rbCardFeatureIcons?.FreeRoamlikeHome}
                    </span>
                  </div>
                </div>
              </div>
            )}

          {/* Legal Disclaimer */}
          <div className="mb-8">
            <button
              className="text-[#DA291C] text-left hover:underline focus:outline-none focus:ring-2 focus:ring-[#DA291C] focus:ring-opacity-50 transition-colors duration-200"
              onClick={toggleDisclaimer}
              aria-label={
                cardData?.data?.card_details?.additional_info?.legal_disclaimer
              }
            >
              {disclaimerExpanded
                ? cardData?.data?.card_details?.additional_info?.legal_disclaimer?.replace(
                    '[+]',
                    '[-]'
                  )
                : cardData?.data?.card_details?.additional_info
                    ?.legal_disclaimer || '[+] Legal disclaimer'}
            </button>

            {disclaimerExpanded &&
              (userProductName ===
              rb_common?.commonString?.legendProductName ? (
                /* ------------------------ START: Updated block ------------------------ */
                <div className="mt-4 pl-4 animate-fade-in">
                  <div
                    aria-label={
                      cardData?.data?.card_details?.additional_info
                        ?.legal_disclaimer_text
                    }
                    className="text-sm text-gray-600"
                  >
                    {(() => {
                      const rawText =
                        cardData?.data?.card_details?.additional_info
                          ?.legal_disclaimer_text || '';

                      // Decode the entity-encoded HTML you receive (e.g., &lt;table&gt;)
                      const decodeEntities = (s: string) =>
                        s
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>')
                          .replace(/&amp;/g, '&')
                          .replace(/&quot;/g, '"')
                          .replace(/&#39;/g, "'");

                      // Table renderer
                      const renderTable = (
                        tableEl: HTMLElement,
                        key: string
                      ) => {
                        const rows: React.ReactNode[] = [];
                        let r = 0;
                        const trs = tableEl.querySelectorAll(
                          ':scope > tr, thead > tr, tbody > tr'
                        );
                        trs.forEach((tr) => {
                          const cells: React.ReactNode[] = [];
                          let c = 0;
                          Array.from(tr.children).forEach((td) => {
                            const isTh = td.tagName.toLowerCase() === 'th';
                            const Tag: any = isTh ? 'th' : 'td';
                            cells.push(
                              <Tag
                                key={`c-${c++}`}
                                className="border border-gray-400 p-3 align-top text-[13px] text-gray-800"
                              >
                                {parseTextWithHTML(
                                  (td as HTMLElement).innerHTML
                                )}
                              </Tag>
                            );
                          });
                          rows.push(
                            <tr
                              key={`r-${r++}`}
                              className="odd:bg-white even:bg-white"
                            >
                              {cells}
                            </tr>
                          );
                        });
                        return (
                          <div key={key} className="my-3 overflow-x-auto">
                            <table className="min-w-[720px] w-full border border-gray-400 text-left">
                              <tbody>{rows}</tbody>
                            </table>
                          </div>
                        );
                      };

                      // 1) Decode
                      const decoded = decodeEntities(rawText);

                      // 2) Split into segments that either are <table>…</table> or plain text around them
                      //    The regex keeps the <table> blocks in the result (capturing group).
                      const segments = decoded
                        .split(/(?=<table\b[^>]*>[\s\S]*?<\/table>)/i)
                        .flatMap((seg) =>
                          seg.split(/(<table\b[^>]*>[\s\S]*?<\/table>)/i)
                        )
                        .filter(Boolean);

                      // 3) Render each segment: tables via renderTable; everything else via your previous method
                      const output: React.ReactNode[] = [];
                      let k = 0;

                      segments.forEach((seg) => {
                        const isTable = /^<table\b/i.test(seg.trim());
                        if (isTable) {
                          // Parse the single table fragment and render
                          if (typeof DOMParser !== 'undefined') {
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(
                              `<div>${seg}</div>`,
                              'text/html'
                            );
                            const tableEl = doc.querySelector(
                              'table'
                            ) as HTMLElement | null;
                            if (tableEl)
                              output.push(renderTable(tableEl, `tb-${k++}`));
                          } else {
                            // SSR fallback: show a plain text note
                            output.push(
                              <p key={`tb-${k++}`} className="mb-2">
                                [Table omitted on server]
                              </p>
                            );
                          }
                        } else {
                          // Render paragraphs using parseTextWithHTML with link styling
                          const paragraphs = seg.split(/\n{2,}/);
                          paragraphs.forEach((para) => {
                            output.push(
                              <div
                                key={`p-${k++}`}
                                className="text-sm text-gray-600 mb-2"
                                aria-label={para}
                              >
                                {parseTextWithHTML(
                                  para,
                                  'text-[#2F6FA7] hover:underline'
                                )}
                              </div>
                            );
                          });
                        }
                      });

                      return output;
                    })()}
                  </div>
                </div>
              ) : (
                <div className="mt-4 pl-4 animate-fade-in">
                  <div
                    aria-label={
                      cardData?.data?.card_details?.additional_info
                        ?.legal_disclaimer_text
                    }
                    className="text-sm text-gray-600"
                  >
                    {(() => {
                      const rawText =
                        cardData?.data?.card_details?.additional_info
                          ?.legal_disclaimer_text || '';

                      const paragraphs = rawText.split(/\n{2,}/);

                      return paragraphs.map((para: string, idx: number) => {
                        return (
                          <div
                            key={idx}
                            className="text-sm text-gray-600 mb-2"
                            aria-label={para}
                          >
                            {parseTextWithHTML(
                              para,
                              'text-[#2F6FA7] hover:underline'
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ))}
          </div>

          {/* Benefits Guide Button - Material Design */}
          <div className="text-center mb-12 px-2" aria-label="Additional Info">
            <Button
              ariaLabel={cardData?.data?.card_details?.additional_info?.text}
              variant="solid"
              onClick={handleBenefitsGuide}
              className="inline-flex h-12 sm:h-16 px-6 sm:px-12 py-3 sm:py-5 justify-center items-center gap-3 bg-[#DA291C] hover:bg-[#b32218] text-white font-medium text-sm sm:text-lg tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 w-full sm:w-auto max-w-xs"
            >
              {cardData?.data?.card_details?.additional_info?.text ||
                'See Benefits guide'}
            </Button>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <CommanModal
          isOpen={showExitModal}
          onClose={handleStay}
          closeIcon={false}
          jsxContent={
            <div className="text-center">
              <h3
                aria-label={rb_common?.commonString?.exitConfirmation}
                className="text-lg font-bold text-center mb-4 text-[#000000]"
              >
                {rb_common?.commonString?.exitConfirmation ||
                  'Leaving Rogers Bank?'}
              </h3>
              <p
                aria-label={rb_common?.commonString?.exitConfirmationMessage}
                className="text-center mb-6 text-[#000000]"
              >
                {rb_common?.commonString?.exitConfirmationMessage ||
                  'You are about to leave the Rogers Bank app and visit our website. Do you want to continue?'}
              </p>
            </div>
          }
          buttonText={rb_common?.commonString?.stay || 'Stay'}
          extraButtonText={rb_common?.commonString?.continue || 'Continue'}
          onExtraButtonClick={handleContinue}
        />
      )}
    </>
  );
};

export default CardFeatures;
